import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Grid, LinearProgress } from "@material-ui/core";
import AppButton from "../../components/CustomButtons/AppButton";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "reactstrap";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import AppConfig, { ACCOUNTING_TYPE, BILLING_STATUS } from "../../config/AppConfig";
import AppColor from "../../utilities/AppColor";
import { Link, useLocation } from "react-router-dom";
import { Info } from "@material-ui/icons";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TablePaginationActions from "./Component/Table/TablePaginationActions";
import AppTable from "./Component/Table/AppTable";
import AppUtil from "../../utilities/AppUtil";
import {
    fetchBillingPaging,
    reduceCreateBilling,
    reduceExportBilling,
    reduceInvoiceBilling
} from "../../store/slices/billing";
import AppDialog from "../../components/Dialog/AppDialog";
import { Formik, Field } from "formik";
import * as Yup from 'yup';
import { fetchClientList } from "../../store/slices/client";
import { getAuthInfo } from "../../store/slices/auth";
import CustomDatePicker from "../../components/Input/CustomDatePicker";
import moment from "moment";
import AppSelect from 'components/Select/AppSelect';

const useStyles = makeStyles(() => ({
    input: {
        margin: '0.5rem 0',
        minHeight: 42,
        width: '100%',
        '&:hover': {
            borderColor: AppColor.mainColor
        },
        '&:focus': {
            borderColor: AppColor.mainColor
        },
        '& .react-daterange-picker__wrapper': {
            borderColor: AppColor.mainColor,
            borderRadius: 4,
            borderWidth: 2,
            padding: '0 0.5rem'
        },
        '& .react-daterange-picker__inputGroup': {
            textAlign: 'center'
        }
    },
    select: {
        padding: '0px 10px',
        border: `1px solid ${AppColor.mainColor}`,
    },
    label: {
        fontSize: '14px',
        color: AppColor.black,
        fontWeight: '700'
    }
}))

const headCells = [
    {
        id: "startDate",
        numeric: "left",
        disablePadding: false,
        label: "label.start_date",
        sort: true,
    },
    {
        id: "endDate",
        numeric: "left",
        disablePadding: false,
        label: "label.end_date",
        sort: true,
    },
    {
        id: "name",
        numeric: "left",
        disablePadding: false,
        label: "label.participantName",
        sort: true,
    },
    {
        id: "totalPrice",
        numeric: "left",
        disablePadding: false,
        label: "label.total",
        sort: true,
    },
    {
        id: "status",
        numeric: "left",
        disablePadding: false,
        label: "label.status",
        sort: true,
    },
    {
        id: "actions",
        numeric: "center",
        disablePadding: false,
        label: "label.actions",
        sort: false,
    },
];

const BillingManagement = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const classes = useStyles();
    const user = useSelector(getAuthInfo);

    const isAdmin = location.pathname.includes('admin');

    const [dateRange, setDateRange] = useState();
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [queries, setQueries] = useState({
        sort: true,
        pageNumber: 1,
        pageSize: 10,
        sortField: "createdDate",
        startDate: '',
        endDate: '',
        clientId: isAdmin ? '' : user.clientId,
        // companyId: user.companyId,
        billingStatus: ''
    });
    const [totalElements, setTotalElements] = useState(0);
    const [data, setData] = useState([]);
    const [clients, setClients] = useState([]);
    const emptyRows = data.length === 0;
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [invoicing, setInvoicing] = useState(0);
    const [exporting, setExporting] = useState(false);

    const statusOptions = [
        {
            value: BILLING_STATUS.PAID,
            label: t('billing_status.PAID')
        },
        {
            value: BILLING_STATUS.NOT_PAID,
            label: t('billing_status.NOT_PAID')
        }
    ]

    const statusOptionsQuickBooks = [
        {
            value: BILLING_STATUS.PAID_QB,
            label: t('billing_status.PAID_QB')
        },
        {
            value: BILLING_STATUS.NOT_PAID,
            label: t('billing_status.NOT_PAID')
        }
    ]

    const _export = async () => {
        setExporting(true);
        const { payload } = await dispatch(reduceExportBilling({
            billingStatus: status,
            ...dateRange && dateRange.length > 1 && {
                startDate: AppUtil.formatDate(dateRange[0], 'YYYY/MM/DD'),
                endDate: AppUtil.formatDate(dateRange[1], 'YYYY/MM/DD')
            }
        }));
        setExporting(false);
        if (payload) {
            _loadData();
        }
    }

    const _invoice = async (id) => {
        setInvoicing(id);
        const { payload } = await dispatch(reduceInvoiceBilling(id));
        setInvoicing(0);
        if (payload) {
            _loadData();
        }
    }

    const _submit = async ({ startDate, endDate }) => {
        setSubmitting(true);
        const { payload } = await dispatch(reduceCreateBilling({
            startDate: AppUtil.formatDate(startDate, 'YYYY/MM/DD'),
            endDate: AppUtil.formatDate(endDate, 'YYYY/MM/DD'),
            companyId: user.companyId
        }));
        setSubmitting(false);
        if (payload) {
            setShowCreateModal(false);
            _loadData();
        }
    }

    const _billingStatusChange = (event) => {
        setStatus(event.target.value);
        const newQueries = {
            ...queries,
            billingStatus: event.target.value
        }
        _loadData(newQueries);
    }

    const _clientChange = (event) => {
        const newQueries = {
            ...queries,
            clientId: event.target.value
        }
        _loadData(newQueries);
    }

    const _dateChange = (event) => {
        setDateRange(event);
        const newQueries = {
            ...queries,
            startDate: event ? AppUtil.formatDate(event[0], 'YYYY/MM/DD') : '',
            endDate: event ? AppUtil.formatDate(event[1], 'YYYY/MM/DD') : ''
        }
        _loadData(newQueries);
    }

    const handleChangePage = (event, newPage) => {
        _loadData({ ...queries, pageNumber: newPage + 1 });
        AppUtil.scrollToTopById("main-panel");
    };

    const handleChangeRowsPerPage = (event) => {
        _loadData({
            ...queries,
            pageNumber: 1,
            pageSize: parseInt(event.target.value),
        });
        AppUtil.scrollToTopById("main-panel");
    };

    const onRequestSort = (event, property) => {
        _loadData({
            ...queries,
            sortField: property,
            sort:
                property === queries.sortField ? queries.sort !== true : true,
        });
    };

    const _loadData = async ({ pageNumber, pageSize, sort, sortField, clientId, billingStatus, startDate, endDate } = {}) => {
        const newQueries = {
            ...queries
        };
        if (clientId !== undefined) {
            newQueries.clientId = clientId;
        }
        if (billingStatus !== undefined) {
            newQueries.billingStatus = billingStatus;
        }
        if (startDate !== undefined) {
            newQueries.startDate = startDate;
        }
        if (endDate !== undefined) {
            newQueries.endDate = endDate;
        }
        if (pageNumber) {
            newQueries.pageNumber = pageNumber;
        }
        if (pageSize) {
            newQueries.pageSize = pageSize;
        }
        if (sortField) {
            newQueries.sortField = sortField;
        }
        newQueries.sort = !(!sort || false);
        setLoading(true);
        dispatch(fetchBillingPaging(newQueries))
            .then(({ payload }) => {
                setLoading(false);
                if (payload) {
                    setQueries(newQueries);
                    setTotalElements(payload.totalElements);
                    setData(payload.content);
                }
            });
    };

    const _loadClients = async () => {
        const { payload } = await dispatch(fetchClientList(user.companyId));
        if (payload) {
            setClients(payload);
        }
    }

    useEffect(() => {
        _loadData();
        if (isAdmin) {
            _loadClients();
        }
    }, []);

    // eslint-disable-next-line react/prop-types
    const renderSelectField = ({ name, placeholder, options, onChange }) => (
        <AppSelect
            name={name}
            placeholder={t(placeholder)}
            options={options}
            onChange={option => {
                const params = {};
                if (option) {
                    params[name] = option.value;
                    onChange && onChange({
                        target: {
                            name: name,
                            value: option.value
                        }
                    })
                } else {
                    params[name] = '';
                    onChange && onChange({
                        target: {
                            name: name,
                            value: ''
                        }
                    })
                }
            }}
            allowOverflow={true}
        />
    );

    return (
        <Box>
            {isAdmin && (
                <AppButton
                    label={t('label.create_billing')}
                    onClick={() => setShowCreateModal(true)}
                />
            )}
            <Grid container spacing={3} className={'mt-2'}>
                <Grid item sm={12} md={6}>
                    <DateRangePicker
                        onChange={_dateChange}
                        // placeholder={placeholder}
                        format={AppConfig.DATE_FORMAT.RANGE_PICKER}
                        yearPlaceholder={'yyyy'}
                        monthPlaceholder={'MM'}
                        dayPlaceholder={'dd'}
                        value={dateRange}
                        className={classes.input}
                    />
                </Grid>
            </Grid>
            {isAdmin && (
                <Grid container spacing={3}>
                    <Grid item sm={12} md={6}>
                        {
                            renderSelectField({
                                name: 'status',
                                placeholder: "label.status",
                                options: user.accountingType === ACCOUNTING_TYPE.QUICKBOOKS ? statusOptionsQuickBooks : statusOptions,
                                onChange: (e) => _billingStatusChange(e)
                            })
                        }
                    </Grid>
                    <Grid item sm={12} md={6}>
                        {
                            renderSelectField({
                                name: 'client',
                                placeholder: "label.participant",
                                options: clients.map(item => {
                                    return {
                                        value: item.id,
                                        label: `${item.organizationName ? item.organizationName : `${item.firstName} ${item.lastName}`} (${item.personalInfoEmail})`
                                    };
                                }),
                                onChange: (e) => _clientChange(e)
                            })
                        }
                    </Grid>
                </Grid>
            )}
            {!isAdmin && (
                <Box mt={2} display="flex" justifyContent="center">
                    <AppButton
                        label={t('label.export_csv_file')}
                        isLoading={exporting}
                        disabled={exporting}
                        onClick={() => _export()}
                    />
                </Box>
            )}
            <Box mt={2} />
            <AppTable
                onRequestSort={onRequestSort}
                order={queries.sort === true ? "asc" : "desc"}
                orderBy={queries.sortField}
                rowCount={data.length}
                headCells={headCells.filter(item => {
                    return isAdmin || (item.id !== 'name' && item.id !== 'status')
                })}
            >
                <TableBody>
                    {loading && (<TableRow>
                        <TableCell colSpan={isAdmin ? headCells.length + 2 : headCells.length} padding="none">
                            <LinearProgress />
                        </TableCell>
                    </TableRow>)}
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>{AppUtil.formatDate(row.startDate, AppConfig.DATE_FORMAT.DEFAULT)}</TableCell>
                            <TableCell>{AppUtil.formatDate(row.endDate, AppConfig.DATE_FORMAT.DEFAULT)}</TableCell>
                            {isAdmin && (
                                <TableCell>{row.clientName}</TableCell>
                            )}
                            <TableCell>${AppUtil.formatMoney(row.totalPrice)}</TableCell>
                            {isAdmin && (
                                <TableCell>
                                    <Chip
                                        style={{
                                            backgroundColor: (BILLING_STATUS.PAID === row.billingStatus || BILLING_STATUS.PAID_QB === row.billingStatus)
                                                ? AppColor.green
                                                : AppColor.red,
                                            color: AppColor.white
                                        }}
                                        size="small"
                                        label={t(`billing_status.${row.billingStatus}`)}
                                    />
                                </TableCell>
                            )}
                            <TableCell>
                                <Box display="flex" alignItems="center" justifyContent="center">
                                    {isAdmin && (
                                        <Button
                                            variant="text"
                                            size="large"
                                            onClick={() => _invoice(row.id)}
                                            disabled={invoicing === row.id || row.billingStatus === BILLING_STATUS.PAID || row.billingStatus === BILLING_STATUS.PAID_QB}
                                        >
                                            {invoicing === row.id ? (
                                                <Spinner color="primary" style={{
                                                    width: '1rem',
                                                    height: '1rem'
                                                }} />
                                            ) : (
                                                <i style={{ fontSize: '20px' }} className="fas fa-file-invoice-dollar" />
                                            )}
                                        </Button>
                                    )}
                                    <Link
                                        to={`${isAdmin ? '/admin' : ''}/billing/${row.id}`}
                                    >
                                        <Button variant="text">
                                            {
                                                isAdmin
                                                    ? <i className="fas fa-edit" />
                                                    : <Info />
                                            }
                                        </Button>
                                    </Link>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}

                    {emptyRows > 0 && (
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={isAdmin ? headCells.length + 1 : headCells.length} className="text-center">
                                {t("label.no_data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            colSpan={headCells.length}
                            count={totalElements}
                            rowsPerPage={queries.pageSize}
                            page={queries.pageNumber - 1}
                            SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelRowsPerPage={`${t("label.rows_per_page")}:`}
                            labelDisplayedRows={({ from, to, count }) =>
                                t("label.from_to", {
                                    from,
                                    to,
                                    count:
                                        count !== -1 ? count : `${t("text.more_than")} ${to}`,
                                })
                            }
                        />
                    </TableRow>
                </TableFooter>
            </AppTable>
            <AppDialog
                open={showCreateModal}
                title={t('title.create_billing')}
                onClose={() => setShowCreateModal(false)}
            >
                <Formik
                    onSubmit={_submit}
                    initialValues={{
                        startDate: '',
                        endDate: '',
                    }}
                    validationSchema={Yup.object().shape({
                        startDate: Yup.date()
                            .required('validation.required'),
                        endDate: Yup.date()
                            .required('validation.required')
                            .when('startDate', (startDate, schema) => (startDate && schema.min(startDate, 'validation.endDateMustBeGreaterThanStartDate'))),
                    })}
                >
                    {({ handleSubmit }) => (
                        <Box>
                            <Box>
                                <Field
                                    label={t('label.start_date')}
                                    component={CustomDatePicker}
                                    name="startDate"
                                    placeholder="dd/mm/yyyy"
                                    formatDateJob={true}
                                    custom={{
                                        format: 'MM/DD/yyyy',
                                        maxDate: moment().add(-1, 'days').toDate()
                                    }}
                                    required
                                />
                                <Field
                                    label={t('label.end_date')}
                                    component={CustomDatePicker}
                                    name="endDate"
                                    placeholder="dd/mm/yyyy"
                                    formatDateJob={true}
                                    custom={{
                                        format: 'MM/DD/yyyy',
                                        maxDate: moment().add(-1, 'days').toDate()
                                    }}
                                    required
                                />
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={5}>
                                <AppButton
                                    label={t('button.back')}
                                    style={{
                                        marginRight: '0.5rem'
                                    }}
                                    mode="outlined"
                                    onClick={() => setShowCreateModal(false)}
                                />
                                <AppButton
                                    label={t('button.save')}
                                    onClick={handleSubmit}
                                    isLoading={submitting}
                                    disabled={submitting}
                                />
                            </Box>
                        </Box>
                    )}
                </Formik>
            </AppDialog>
        </Box>
    )
}

export default BillingManagement;
